/**
 * Installing the console as an application on this device.
 *
 * A browser will offer this in its own address bar, in a menu most operators
 * never open, with wording that changes between releases. That is the same
 * shape as every other capability this console has had to write down: reachable
 * in principle, found by nobody. So it gets a control, and the control is
 * offered only while it can do something.
 *
 * The four states, and why each is not the others:
 *
 *   - **`ready`** — the browser fired `beforeinstallprompt`, which means it has
 *     already decided this page qualifies. The event is the *only* honest signal
 *     from a Chromium browser; inferring installability from a manifest and a
 *     service worker would be this code guessing at criteria that belong to the
 *     browser and differ per engine.
 *   - **`manual`** — the browser installs this page but will not let a page ask.
 *     iOS is the whole of this case: no WebKit browser has ever fired
 *     `beforeinstallprompt`, and Add to Home Screen lives in the share sheet
 *     where only a person can reach it. The console cannot press it, but it can
 *     say where it is, and the gesture is short enough to be worth saying.
 *   - **`installed`** — either the page is running as an installed app
 *     (`display-mode: standalone`, or `navigator.standalone` on iOS) or
 *     `appinstalled` fired while it was open. Nothing is offered: an install
 *     button in an installed app is a button that cannot do anything.
 *   - **`unsupported`** — no event, not iOS, not standalone. Desktop Safari and
 *     Firefox are here: their route is a menu item whose name changes between
 *     releases, and instructions for a browser this operator may not be using
 *     are worse than silence.
 *
 * `manual` is why this hook grew past a boolean. It was previously `unsupported`
 * — the reasoning being that a button which does nothing when pressed is worse
 * than no button — and that reasoning is sound about *buttons* and wrong about
 * *iOS*, where installing is the ordinary way to use a web application and the
 * console was the only thing on the phone that never mentioned it.
 *
 * The prompt may only be shown from a user gesture, and only once per event: the
 * browser discards it after `prompt()` either way, so the stored event is
 * cleared whether the operator accepted or dismissed. If they dismissed it, the
 * browser will fire a fresh one later and the control comes back on its own.
 */

import { useCallback, useEffect, useState } from 'react'

import { isStandaloneApp } from './useImmersive'

/** The event Chromium fires when it is willing to install this page. Not in the
 *  DOM lib, because it is not standardised. */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export type InstallState = 'unsupported' | 'ready' | 'manual' | 'installed'

/**
 * Whether this is an iOS device, where installing is a share-sheet gesture.
 *
 * Two spellings, because iPadOS 13 began reporting itself as a Mac: an iPad's
 * user agent says `Macintosh` and its platform says `MacIntel`, and the only
 * thing separating it from a real desktop is that a Mac's `maxTouchPoints` is
 * zero. Testing the user agent alone therefore misses every iPad, which is the
 * device most likely to be running an operations console in the first place.
 *
 * This deliberately does not try to tell Safari from the other iOS browsers.
 * They are all WebKit, all reach Add to Home Screen through the same share
 * sheet, and a check that split them would be a user-agent guess in a place
 * where being wrong means showing instructions for a control that is not there.
 */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return true
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
}

export function useInstallApp(): { state: InstallState; install: () => Promise<void> } {
  const [prompt, setPrompt] = useState<InstallPromptEvent | null>(null)
  // The same test the band uses to decide it is redundant — one definition of
  // "this window is the app", so an install button and a letterhead cannot
  // disagree about whether the console is installed.
  const [installed, setInstalled] = useState(isStandaloneApp)

  useEffect(() => {
    const onAvailable = (e: Event) => {
      // Cancelling the browser's own mini-infobar is what moves the decision to
      // this console's control rather than leaving two offers on screen.
      e.preventDefault()
      setPrompt(e as InstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setPrompt(null)
    }
    window.addEventListener('beforeinstallprompt', onAvailable)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onAvailable)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const install = useCallback(async () => {
    if (!prompt) return
    // Cleared first, and deliberately: the event is single-use, and leaving it
    // in state would offer a second press that the browser answers with an
    // exception rather than a dialog.
    setPrompt(null)
    await prompt.prompt()
    // The outcome is awaited and not acted on. Accepting fires `appinstalled`,
    // which is what flips the state; taking `accepted` as proof here would claim
    // an install the browser has not finished. Dismissing needs no undo — the
    // browser decides when to offer again, and the control returns with it.
    await prompt.userChoice
  }, [prompt])

  const state: InstallState = installed
    ? 'installed'
    : prompt
      ? 'ready'
      : isIOS()
        ? 'manual'
        : 'unsupported'
  return { state, install }
}
